<template>
  <v-container id="gri-items" class="custom-container">
    <!-- Error Alert -->
    <v-alert
      v-if="showError"
      type="error"
      dismissible
      @input="showError = false"
    >
      Returned quantity cannot exceed ordered quantity!
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="griItems"
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style="border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="{ item, index }">
        <tr :key="index">
          <td>
            <v-autocomplete
              v-model="item.product_code"
              required
              dense
              outlined
              clearable
              :items="productCodes"
              item-text="product_code"
              item-value="product_code"
              @change="onProductSelect(item)"
            ></v-autocomplete>
          </td>
          <td>
            <v-autocomplete
              v-model="item.batch_id"
              required
              dense
              outlined
              clearable
              :items="getBatchesForProduct(item.product_code)"
              item-text="batch_code"
              item-value="id"
            ></v-autocomplete>
          </td>

          <td>
            <v-text-field
              class="narrow-field"
              v-model="item.quantity_returned"
              type="number"
              dense
              min="0"
              :max="getMaxReturned(item)"
              outlined
              @input="onQuantityInput(item)"
              @change="onQuantityInput(item)"
              :hint="`Max: ${getMaxReturned(item)}`"
              persistent-hint
            ></v-text-field>
          </td>

          <td>
            <v-text-field
              class="narrow-field-price"
              v-model="item.unit_price"
              type="number"
              dense
              min="0"
              outlined
              @input="updateValues(item)"
            ></v-text-field>
          </td>
          <td>
            <v-select
              v-model="item.condition"
              :items="conditions"
              dense
              outlined
              clearable
              placeholder="Select Condition"
              item-text="text"
              item-value="value"
            ></v-select>
          </td>
          <td>
            <v-text-field
              v-model="item.reason"
              dense
              outlined
              placeholder="Reason for Return"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.total_price"
              dense
              outlined
              disabled
            ></v-text-field>
          </td>
          <td class="pb-5">
            <v-icon size="30" @click="deleteItem(index)" color="red">
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-row class="mt-0">
      <v-col v-if="!addingNewRow" class="text-left">
        <v-icon size="40" color="blue" @click="addNewRow">mdi-plus</v-icon>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { GoodsReturnService } from "@/services/goodsReturn-service.js";
import { checkError } from "@/config/errors.js";
export default {
  props: {
    initialGriItems: Array,
    products: { type: Array, required: true },
  },
  data() {
    return {
      headers: [
        { text: "Item Code", value: "product_code" },
        { text: "Batch", value: "batch_id" },
        { text: "Quantity Returned", value: "quantity_returned" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Condition", value: "condition" },
        { text: "Reason", value: "reason" },
        { text: "Total (Rs.)", value: "total_price" },
        { text: "", value: "actions", sortable: false },
      ],
      griItems: [],
      batches: [],
      addingNewRow: false,
      totalAmount: 0,
      search: "",
      showError: false,
      conditions: [
        { text: "Good", value: "Good" },
        { text: "Damaged", value: "Damaged" },
        { text: "Defective", value: "Defective" },
      ],
    };
  },
  watch: {
    initialGriItems: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.griItems = [...newVal];
        } else {
          this.griItems.push(this.getDefaultRow());
        }
      },
    },
    griItems: {
      deep: true,
      handler(newItems) {
        newItems.forEach((item) => {
          const max = this.getMaxReturned(item);
          if (Number(item.quantity_returned) > max) {
            item.quantity_returned = max;
            this.showError = true;
          }
        });
        const total = this.calculateTotalAmount();
        this.$emit("total", total);
        this.$emit("items", newItems);
      },
    },
  },
  computed: {
    productCodes() {
      return [...new Set(this.products.map((p) => p.product_code))];
    },
  },
  created() {
    this.fetchBatches();
  },
  methods: {
    async fetchBatches() {
      try {
        this.loading = true;
        const batches = await GoodsReturnService.getBatchList(this.search);
        this.batches = batches.data.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    getMaxReturned(item) {
      const product = this.products.find(
        (p) =>
          p.product_code === item.product_code && p.batch_id === item.batch_id
      );
      return product ? Number(product.quantity_received) : 0;
    },

    onProductSelect(item) {
      const selectedProduct = this.products.find(
        (product) => product.product_code === item.product_code
      );
      if (selectedProduct) {
        this.quantity_returned = selectedProduct.quantity_returned;
        item.unit_price = selectedProduct.unit_price || 0;
        item.tax_percent = selectedProduct.tax_percent || 0;
        item.discount_percent = selectedProduct.discount_percent || 0;
        this.updateValues(item);
      }
    },

    updateValues(item) {
      const product = this.products.find(
        (p) =>
          p.product_code === item.product_code && p.batch_id === item.batch_id
      );
      if (product) {
        const max = Number(product.quantity_received);
        if (Number(item.quantity_returned) > max) {
          this.showError = true;
          item.quantity_returned = max;
        } else {
          this.showError = false;
        }
        item.unit_price = Number(product.unit_price);
        item.total_price = (item.quantity_returned * item.unit_price).toFixed(
          2
        );
      }
    },

    onQuantityInput(item) {
      const max = this.getMaxReturned(item);
      if (Number(item.quantity_returned) > max) {
        item.quantity_returned = max;
        this.showError = true;
      } else {
        this.showError = false;
      }
      this.updateValues(item);
    },
    deleteItem(index) {
      this.griItems.splice(index, 1);
    },
    addNewRow() {
      this.addingNewRow = true;
      this.$nextTick(() => {
        this.griItems.push(this.getDefaultRow());
        this.addingNewRow = false;
      });
    },
    getDefaultRow() {
      return {
        product_code: "",
        stock_id: "",
        description: "",
        quantity_returned: 0,
        unit_price: 0,
        condition: "",
        reason: "",
        total_price: 0,
      };
    },
    calculateTotalAmount() {
      this.totalAmount = 0;
      this.griItems.forEach((item) => {
        this.totalAmount += parseFloat(item.total_price || 0);
      });
      return this.totalAmount;
    },
    getBatchesForProduct(productCode) {
      return this.products
        .filter((p) => p.product_code === productCode)
        .map((p) => ({
          id: p.batch_id,
          batch_code: p.batch_code,
        }));
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}
.custom-container {
  width: 100%;
  max-width: 100%;
  padding: 0 !important;
  margin: 0 !important;
}
.v-data-table td {
  padding: 8px !important;
  text-align: center;
}
.v-data-table th {
  padding: 8px !important;
  text-align: center;
}

.narrow-field {
  max-width: 200px;
  min-width: 200px;
  width: 100px;
  margin: 0 auto;
}

.narrow-field-price {
  max-width: 130px;
  min-width: 150px;
  width: 150px;
  margin: 0 auto;
}

.narrow-field-reason {
  max-width: 160px;
  min-width: 170px;
  width: 170px;
  margin: 0 auto;
}
</style>
